<template>
  <form
    class="RunFormView"
    :class="{
      'RunFormView--hasMultipleCrews': hasMultipleCrews
    }"
    @submit.prevent="$emit('submit', payload)"
  >
    <fieldset
      class="RunFormView__fieldset"
      :disabled="isRequesting"
    >
      <SheetHeader
        v-bind="{ title }"
        @cancel="$emit('cancel')"
      />

      <div class="RunFormView__content">
        <Transition>
          <div
            v-if="!isRequesting"
            class="RunFormView__fieldGrid"
          >
            <FormField
              v-if="hasMultipleCrews"
              gridArea="crews"
              :label="form.crewIds.length > 1 ? 'Crews' : 'Crew'"
              isFieldset
            >
              <FormChecklist
                v-model="form.crewIds"
                :options="crewOptions"
              />
            </FormField>

            <FormField
              label="Court"
              gridArea="court"
              isFieldset
            >
              <CourtCarouselSelect
                v-model="form.courtId"
                v-bind="{courts}"
              />
            </FormField>

            <FormField
              label="Date"
              gridArea="date"
            >
              <DateInput
                v-model="form.date"
                name="date"
                isRequired
              />
            </FormField>

            <FormField
              label="Time"
              gridArea="time"
            >
              <TimeInput
                v-model="form.time"
                name="time"
                isRequired
              />
            </FormField>

            <FormField
              label="Duration"
              gridArea="duration"
            >
              <DurationRangeInput
                v-model="form.duration"
                v-bind="{startDate}"
              />
            </FormField>

            <FormField
              label="Minimum numbers"
              gridArea="numbers"
            >
              <MinPlayerCountControl
                v-model="form.minPlayerCount"
              />
            </FormField>

            <FormField
              label="Player cap"
              gridArea="maxNumbers"
            >
              <MaxPlayerCountInput
                v-model="form.maxPlayerCount"
                :minPlayerCount="form.minPlayerCount"
              />
            </FormField>

            <FormField
              label="RSVP"
              gridArea="rsvp"
            >
              <RsvpRangeInput
                v-model="form.rsvpDayCount"
              />
            </FormField>
          </div>
        </Transition>
      </div>
    </fieldset>
  </form>
</template>

<script lang="ts" setup>
import CourtCarouselSelect from "@/components/CourtCarouselSelect.vue";
import DateInput from "@/components/DateInput.vue";
import DurationRangeInput from "@/components/DurationRangeInput.vue";
import { FormChecklistOption } from "@/components/FormChecklist.types";
import FormChecklist from "@/components/FormChecklist.vue";
import FormField from "@/components/FormField.vue";
import MaxPlayerCountInput from "@/components/MaxPlayerCountInput.vue";
import MinPlayerCountControl from "@/components/MinPlayerCountControl.vue";
import RsvpRangeInput from "@/components/RsvpRangeInput.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import TimeInput from "@/components/TimeInput.vue";
import { supabaseClient } from "@/lib/database/supabase";
import useAuthStore from "@/stores/useAuthStore";
import useCommissionerCourtStore from "@/stores/useCommissionerCourtStore";
import useCurrentPlayerStore from "@/stores/useCurrentPlayerStore";
import { TZDate } from "@date-fns/tz";
import { InsertRunWithCrewsArgs, UpdateRunWithCrewsArgs } from "@shared/lib/database/function.types";
import { Run, RunWithMaybeCourt } from "@shared/lib/database/query.types";
import { addDays, addHours, differenceInMinutes, format, parseISO, set } from "date-fns";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref } from "vue";

const isRequesting = defineModel<boolean>("isRequesting", {
  default: true,
});

const emit = defineEmits<{
  cancel: [],
  submit: [InsertRunWithCrewsArgs | UpdateRunWithCrewsArgs | undefined],
}>();

const {
  runId,
} = defineProps<{
  runId?: string,
  title: string,
}>();

const {
  commissionerCrews,
} = storeToRefs(useCurrentPlayerStore());

const {
  fetchCrewMemberships,
} = useCurrentPlayerStore();

const {
  fetchCommissionerCourts,
} = useCommissionerCourtStore();

const {
  commissionerCourts,
} = storeToRefs(useCommissionerCourtStore());

const {
  currentUser,
} = storeToRefs(useAuthStore());

const historyStateRun = ref<Run | null>(history.state.run);
const historyStateCrewIds = ref<Array<string>>(history.state.crewIds || []);
const existingRun = ref<RunWithMaybeCourt | null>(null);
const existingCrewIds = ref<Array<string> | null>(null);

const run = computed(() => existingRun.value || historyStateRun.value);
const crewIds = computed(() => existingCrewIds.value || historyStateCrewIds.value);

const form = ref<{
  runId: string | null,
  crewIds: Array<string>,
  courtId: string | null,
  seasonId: string | null,
  date: string,
  time: string,
  duration: number,
  minPlayerCount: number | null,
  maxPlayerCount: number | null,
  rsvpDayCount: number | null,
}>(getInitialForm());

onBeforeMount(async () => {
  if (runId) {
    await fetchRun(runId);
    form.value = getInitialForm();
  }

  try {
    await Promise.all([fetchCommissionerCrews(), fetchCourts()]);
  } catch (err) {
    alert((err as Error).message);
    emit("cancel");
  }

  isRequesting.value = false;
});

const hasMultipleCrews = computed(() => commissionerCrews.value.length > 1);
const courts = computed(() => {
  if (existingRun.value?.court && !commissionerCourts.value.some((court) => court.id === existingRun.value?.court_id)) {
    return [existingRun.value.court, ...commissionerCourts.value];
  }

  return commissionerCourts.value;
});
const court = computed(() => courts.value.find((court) => court.id === form.value.courtId));

const startDate = computed(() => {
  if (!court.value || !form.value.date || !form.value.time) return;

  const [
    year,
    month,
    day,
  ] = form.value.date.split("-").map(parseFloat);
  const [hours, minutes] = form.value.time.split(":").map(parseFloat);

  return new TZDate(year, month - 1, day, hours, minutes, court.value.timezone);
});
const endDate = computed(() => {
  if (!startDate.value || !form.value.duration) return;

  return addHours(startDate.value, form.value.duration);
});

const payload = computed((): InsertRunWithCrewsArgs | UpdateRunWithCrewsArgs | undefined => {
  if (!currentUser.value || form.value.crewIds.length === 0 || !form.value.courtId || !startDate.value) return;

  return {
    run_id: form.value.runId || undefined,
    court_id: form.value.courtId,
    start_at: startDate.value?.toISOString(),
    end_at: endDate.value?.toISOString(),
    crew_ids: form.value.crewIds,
    season_id: form.value.seasonId || undefined,
    min_player_count: form.value.minPlayerCount || undefined,
    max_player_count: form.value.maxPlayerCount || undefined,
    rsvp_day_count: form.value.rsvpDayCount || undefined,
  };
});

// TODO: extract into crew component
const crewOptions = computed((): Array<FormChecklistOption<string | null>> => {
  return [
    ...commissionerCrews.value.map((crew, i) => {
      return {
        name: `crew${1 + i}`,
        label: crew.name,
        value: crew.id,
      };
    }),
  ];
});

function getInitialForm () {
  const startDate = run.value?.start_at
    ? parseISO(run.value.start_at)
    : addDays(set(new Date(), {
      hours: 8,
      minutes: 0,
      seconds: 0,
    }), 2);
  const endDate = run.value?.end_at ? parseISO(run.value.end_at) : addHours(startDate, 2);
  const duration = differenceInMinutes(endDate, startDate) / 60;

  return {
    runId: null,
    crewIds: getInitialCrewIds(),
    courtId: null,
    seasonId: run.value?.season_id || null,
    date: format(startDate, "yyyy-MM-dd"),
    time: format(startDate, "HH:mm"),
    duration,
    minPlayerCount: run.value?.min_player_count || null,
    maxPlayerCount: run.value?.max_player_count || null,
    rsvpDayCount: run.value?.rsvp_day_count || null,
  };
}

function getInitialCrewIds () {
  if (crewIds.value.length > 0) {
    return commissionerCrews.value
      .filter((crew) => crewIds.value.includes(crew.id))
      .map((crew) => crew.id);
  }

  return commissionerCrews.value.length === 1 ? [commissionerCrews.value[0].id] : [];
}

async function fetchRun (runId: string) {
  const {
    data: _run,
    error,
  } = await supabaseClient.from("runs")
    .select("*, crew_runs(*), court:courts(*)")
    .eq("id", runId)
    .maybeSingle();

  if (_run) {
    existingRun.value = _run;
    existingCrewIds.value = _run.crew_runs.map((crewRun) => crewRun.crew_id);
  } else if (error) {
    alert(error.message);
  }
}

async function fetchCommissionerCrews () {
  await fetchCrewMemberships();

  form.value.crewIds = getInitialCrewIds();
}

async function fetchCourts () {
  await fetchCommissionerCourts();

  form.value.courtId = courts.value.find((court) => {
    return run.value?.court_id === court.id;
  })?.id || courts.value.at(0)?.id || null;
}
</script>

<style lang="scss" scoped>
.RunFormView {
  position: absolute;
  inset: 0;
}

.RunFormView__fieldset {
  display: grid;
  align-content: start;
  height: 100%;
}

.RunFormView__content {
  position: relative;

  min-height: 0;
  padding-bottom: var(--safeAreaInsetBottom);
  overflow-y: auto;
}

.RunFormView__fieldGrid {
  display: grid;
  grid-template-areas: "court court"
    "date time"
    "duration duration"
    "numbers numbers"
    "maxNumbers maxNumbers"
    "rsvp rsvp";
  grid-template-columns: 1fr auto;
  gap: 24px 16px;
  padding: 24px 16px;

  transition: 350ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }

  .RunFormView--hasMultipleCrews & {
    grid-template-areas: "crews crews"
      "court court"
      "date time"
      "duration duration"
      "numbers numbers"
      "maxNumbers maxNumbers"
      "rsvp rsvp";
  }
}
</style>
